import { Minus, Plus, ShieldOff } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { addDays, isPast } from 'date-fns';
import { useState } from 'react';

import { GetPortalQuery as GetPortal } from '__generated__/graphql';

import { Avatar } from '../Common/Avatar';
import { Container, NavItem, Section } from '../Common/Layout';
import { resizedImageUrl } from '../../common/helpers';
import { useLogout } from '../../common/Auth/useAuth';
import { useNavigation } from '../Common/useNavigation';
import DateRange from '../../common/Common/DateRange';
import GetPortalQuery from './GetPortalQuery';
import ReviewSection from '../Review/ReviewSection';
import UI from '../../common/UI';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useQuery from '../../api/useQuery';

const PortalPage = () => {
  const { data } = useQuery(GetPortalQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const user = data?.session.user;

  if (!user) {
    return (
      <UI.PageLoader />
    );
  }

  return (
    <Portal user={user} />
  );
};

type User = GetPortal['session']['user'];

interface PortalProps {
  user: User;
}

const Portal = ({ user }: PortalProps) => {
  const { t } = useTranslation();
  const { r } = useNavigation();
  const { parseDate } = useLocale();
  const [logout] = useLogout();

  useDocumentTitle([
    user.full_name,
    'Atleta',
  ]);

  const isOrganizing = user.project_memberships.length > 0;

  const isPastEvent = (event: { end_date: string; }) => (
    isPast(addDays(parseDate(event.end_date, { timezone: 'UTC' }), 1))
  );
  const inactiveCount = user.registration_summaries.filter(({ event }) => isPastEvent(event)).length;
  const activeCount = user.registration_summaries.length - inactiveCount;

  const [showPast, setShowPast] = useState(false);

  const [pendingReviewSummaryIds, setPendingReviewSummaryIds] = useState(user.registration_summaries.filter(
    (summary) => summary.can_add_review && !summary.review,
  ).map((summary) => summary.id));

  const pendingReviewSummary = user.registration_summaries.find((summary) => summary.id === pendingReviewSummaryIds[0]);

  return (
    <Container>
      <UI.GridContainer sc={{ columns: '80px 1fr' }}>
        <UI.Div>
          <Avatar sportType={user.sport_type} size={80} />
        </UI.Div>
        <UI.Div style={{ alignSelf: 'center' }}>
          <UI.H2 sc={{ mb: 0.5 }}>
            {t('welcome_title', { name: user.first_name })}
          </UI.H2>
          <UI.Paragraph sc={{ color: 'gray.500', mb: 0 }}>
            {isOrganizing ? t('welcome_message_organizer') : t('welcome_message_participant')}
          </UI.Paragraph>
        </UI.Div>
      </UI.GridContainer>
      <UI.HR />
      {isOrganizing && (
        <Section title={t('your_projects')} secondary>
          {user.project_memberships.map((membership) => (
            <ProjectMembershipRow membership={membership} key={membership.id} />
          ))}
        </Section>
      )}

      {pendingReviewSummary && (
        <ReviewSection
          summary={pendingReviewSummary}
          onClose={() => setPendingReviewSummaryIds((ids) => ids.slice(1))}
          refetchQueries={[GetPortalQuery]}
        />
      )}

      <Section title={t('your_registrations')}>
        {activeCount === 0 && (
          <UI.Div
            sc={{ color: 'gray.500', textAlign: 'center', py: 6, px: [4, 6] }}
            style={{ maxWidth: 600, textWrap: 'balance', margin: '0 auto' }}
          >
            <UI.H4 sc={{ fontSize: 4, mb: 1 }}>
              {t('no_upcoming_event')}
            </UI.H4>
            <Trans i18nKey="find_next_event_user_message">
              <UI.Link to={r('Calendar')} sc={{ strong: true }} />
            </Trans>
          </UI.Div>
        )}
        {user.registration_summaries.map((summary) => (
          (showPast || !isPastEvent(summary.event)) && (
            <RegistrationSummaryRow
              summary={summary}
              muted={isPastEvent(summary.event)}
              key={summary.id}
            />
          )
        ))}
        {inactiveCount > 0 && (
          <NavItem
            onClick={() => setShowPast((show) => !show)}
            icon={showPast ? <Minus /> : <Plus />}
            sc={{ background: 'gray.50', py: 3, color: 'link', fontWeight: 500 }}
            title={t(showPast ? 'hide_past_events' : 'show_past_events')}
          >
            {`${t(showPast ? 'hide_past_events' : 'show_past_events')} (${inactiveCount})`}
          </NavItem>
        )}
      </Section>

      <Section title={t('your_account')}>
        <NavItem to={r('Account')} title={t('edit_account')} alignVertical="center">
          <UI.H4>{t('edit_account')}</UI.H4>
          {user.email}
          {user.authenticator_available && !user.authenticator_enabled && (
            <>
              {' '}
              <UI.Icon sc={{ muted: true }}>
                <ShieldOff />
              </UI.Icon>
            </>
          )}
        </NavItem>
      </Section>

      <UI.Div>
        <UI.A onClick={() => logout()} role="button" sc={{ secondary: true, small: true }}>
          {t('log_out')}
        </UI.A>
      </UI.Div>
    </Container>
  );
};

interface ProjectMembershipRowProps {
  membership: User['project_memberships'][0];
}

const ProjectMembershipRow = ({ membership }: ProjectMembershipRowProps) => (
  <NavItem
    href={`b/${membership.project.id}`}
    title={membership.project.organisation_name}
    sc={{ py: 3 }}
    alignVertical="center"
  >
    <UI.H4>
      {membership.project.organisation_name}
    </UI.H4>
  </NavItem>
);

interface RegistrationSummaryRowProps {
  summary: User['registration_summaries'][0];
  muted: boolean;
}

const RegistrationSummaryRow = ({ summary, muted }: RegistrationSummaryRowProps) => {
  const { r } = useNavigation();

  const link = r('Registrations', {
    eventId: summary.event.id,
    projectId: summary.event.project.id,
    participantId: summary.participant.id,
    token: summary.view_token,
  });

  return (
    <NavItem
      to={link}
      title={summary.event.title}
      alignVertical="center"
    >
      <UI.GridContainer sc={{ columns: '40px 1fr', alignVertical: 'center', gutter: 0.5, muted }}>
        <UI.ProfilePic
          sc={{ size: 40, background: summary.event.logo ? 'white' : undefined }}
        >
          {summary.event.logo && (
            <img
              src={resizedImageUrl(summary.event.logo.url, 300, 'webp')}
              alt={summary.event.title}
            />
          )}
          {!summary.event.logo && (
            <UI.Span><UI.Span>{summary.event.title.charAt(0)}</UI.Span></UI.Span>
          )}
        </UI.ProfilePic>
        <UI.Div>
          <UI.H4>{summary.event.title}</UI.H4>
          <DateRange
            start={summary.event.start_date}
            end={summary.event.end_date}
            timezone={summary.event.project.timezone}
          />
        </UI.Div>
      </UI.GridContainer>
    </NavItem>
  );
};

export default PortalPage;
